import { ref } from "vue";
import io from "socket.io-client";
import { authenticatedFetch } from './oauth2-config';

const API_URL = process.env.VUE_APP_API_URL || '__VUE_APP_API_URL__';
const WS_URL = process.env.VUE_APP_WEBSOCKET_URL || '__VUE_APP_WEBSOCKET_URL__';

function getAuthToken() {
  return localStorage.getItem('id_token');
}

const socket = io(WS_URL, {
  secure: true,
  transports: ['websocket'],
  path: '/socket.io',
  auth: { token: getAuthToken() },
  autoConnect: false
});

// Reactive state
const isConnected = ref(false);
const contacts = ref([]);
const presence = ref({});
const filters = ref({ departments: [], companies: [], sources: [] });
const pagination = ref({ total: 0, limit: 50, offset: 0, hasMore: true });
const isLoading = ref(false);
const currentFilters = ref({});

// Call Records state
const callRecords = ref([]);
const callRecordsLoading = ref(false);
const callRecordsPagination = ref({ total: 0, limit: 50, offset: 0, hasMore: false });
const missedCallsCount = ref(0);
const currentCallFilter = ref('all'); // 'all', 'missed', 'incoming', 'outgoing'

// Assignment notification state
const callAssignments = ref([]); // Recent assignment notifications
const callAssignmentCount = ref(0); // Badge count for unread assignments

// User's Call Queue memberships
const userCallQueues = ref([]); // Array of { queue_user_id, queue_email, queue_name }
const selectedCallQueue = ref('personal'); // 'personal' or queue_email
const userCallQueuesLoading = ref(false);

// Photo cache (Blob URLs) - LRU cache with max size to prevent memory leaks
class LRUPhotoCache {
  constructor(maxSize = 500) {
    this.maxSize = maxSize;
    this.cache = new Map();
  }
  get(key) {
    if (!this.cache.has(key)) return undefined;
    // Move to end (most recently used)
    const value = this.cache.get(key);
    this.cache.delete(key);
    this.cache.set(key, value);
    return value;
  }
  set(key, value) {
    if (this.cache.has(key)) {
      this.cache.delete(key);
    } else if (this.cache.size >= this.maxSize) {
      // Evict least recently used (first entry)
      const [oldestKey, oldestValue] = this.cache.entries().next().value;
      URL.revokeObjectURL(oldestValue);
      this.cache.delete(oldestKey);
    }
    this.cache.set(key, value);
  }
  has(key) {
    return this.cache.has(key);
  }
  clear() {
    for (const url of this.cache.values()) {
      URL.revokeObjectURL(url);
    }
    this.cache.clear();
  }
}

const photoCache = new LRUPhotoCache(500);

// Persistent "no photo" cache in localStorage - survives page refresh
// Stores emails of users confirmed to have no photo (TTL: 24 hours)
// Import branding for configurable storage prefix
import { branding } from './oauth2-config';
const NO_PHOTO_CACHE_KEY = `${branding.storagePrefix}_no_photo_cache`;
const NO_PHOTO_CACHE_TTL = 24 * 60 * 60 * 1000; // 24 hours

function getNoPhotoCache() {
  try {
    const cached = localStorage.getItem(NO_PHOTO_CACHE_KEY);
    if (!cached) return { emails: {}, timestamp: Date.now() };
    const data = JSON.parse(cached);
    // Invalidate if older than TTL
    if (Date.now() - data.timestamp > NO_PHOTO_CACHE_TTL) {
      localStorage.removeItem(NO_PHOTO_CACHE_KEY);
      return { emails: {}, timestamp: Date.now() };
    }
    return data;
  } catch {
    return { emails: {}, timestamp: Date.now() };
  }
}

function addToNoPhotoCache(email) {
  try {
    const cache = getNoPhotoCache();
    cache.emails[email.toLowerCase()] = true;
    localStorage.setItem(NO_PHOTO_CACHE_KEY, JSON.stringify(cache));
  } catch {
    // localStorage might be full or disabled - ignore
  }
}

function isInNoPhotoCache(email) {
  if (!email) return true;
  const cache = getNoPhotoCache();
  return cache.emails[email.toLowerCase()] === true;
}

// Socket event handlers
socket.on("connect", () => {
  isConnected.value = true;
  console.log("WebSocket connected");
  // Fetch presence on reconnect if already authenticated
  const idToken = localStorage.getItem('id_token');
  if (idToken && idToken.split('.').length === 3) {
    fetchPresence();
  }
});

socket.on("connect_error", (err) => {
  console.error("WebSocket connection error:", err.message);
  // If auth failed, token may have refreshed — update and retry
  if (err.message === 'Authentication failed' || err.message === 'Authentication required') {
    const token = getAuthToken();
    if (token) {
      socket.auth = { token };
    }
  }
});

socket.on("disconnect", () => {
  isConnected.value = false;
  console.log("WebSocket disconnected");
});

socket.on("contactsUpdated", () => {
  console.log("Contacts updated - refetching...");
  fetchContacts(currentFilters.value, true); // Reset and refetch
});

socket.on("presenceUpdated", (data) => {
  const count = Object.keys(data).length;
  console.log("Presence delta:", count, "changes");
  // Merge delta into existing presence state
  Object.assign(presence.value, data);
});

// Call record created - update missed count and add to list
socket.on("callRecordCreated", (data) => {
  console.log("New call record:", data.id);
  const currentUserId = localStorage.getItem('user_id');
  
  // Check if current user was involved in this call
  const myParticipation = data.participants?.find(p => p.userId === currentUserId);
  
  // Only process if user was a participant
  if (!myParticipation) {
    console.log("Ignoring call - not a participant");
    return;
  }
  
  // Update missed calls count if user missed this call
  if (myParticipation.isMissed) {
    missedCallsCount.value++;
  }
  
  // Prepend to call records if already loaded AND matches current filter
  if (callRecords.value.length > 0) {
    const isMissed = myParticipation.isMissed;
    const direction = myParticipation.direction || (data.organizerId === currentUserId ? 'outgoing' : 'incoming');
    
    // Check if call matches current filter
    const filter = currentCallFilter.value;
    const matchesFilter = filter === 'all' 
      || (filter === 'missed' && isMissed) 
      || (filter === 'incoming' && direction === 'incoming')
      || (filter === 'outgoing' && direction === 'outgoing');
    
    if (matchesFilter) {
      // Transform data to match the format from API
      callRecords.value.unshift({
        id: data.id,
        call_type: data.type,
        start_time: data.startTime,
        duration_seconds: data.duration,
        organizer_name: data.organizerName,
        organizer_id: data.organizerId,
        external_contact_name: data.externalContact?.name,
        external_company_name: data.externalContact?.company,
        answered_by_name: data.answeredBy,
        direction: direction,
        is_missed: isMissed,
        is_answered: !isMissed,
        caller_photo: data.callerPhoto
      });
    }
  }
});

// Call assigned notification - someone assigned a CQ call note to this user
socket.on("callAssigned", (data) => {
  console.log("Call assigned to me:", data);
  callAssignmentCount.value++;
  callAssignments.value.unshift({
    id: Date.now(),
    callId: data.callId,
    callerName: data.callerName,
    queueName: data.queueName,
    assignedByName: data.assignedByName,
    notePreview: data.notePreview,
    timestamp: new Date().toISOString(),
    read: false
  });
  // Keep max 20 notifications
  if (callAssignments.value.length > 20) {
    callAssignments.value = callAssignments.value.slice(0, 20);
  }
});

/**
 * Clear assignment notification count (mark as seen)
 */
function clearAssignmentCount() {
  callAssignmentCount.value = 0;
  callAssignments.value.forEach(a => a.read = true);
}

/**
 * Pending fetch queue: when a reset-fetch is requested while another is in progress,
 * store the params here so it runs immediately after the current one finishes.
 * This prevents the silent-drop bug where concurrent calls were just discarded.
 */
let pendingResetParams = null;

/**
 * Fetch contacts with pagination (reset=true for new search, false for load more)
 */
async function fetchContacts(searchParams = {}, reset = true) {
  // If already loading: queue reset-fetches instead of silently dropping them
  if (isLoading.value) {
    if (reset) {
      pendingResetParams = searchParams;
    }
    // load-more calls are safe to drop (IntersectionObserver will re-trigger)
    return;
  }
  
  try {
    isLoading.value = true;
    pendingResetParams = null; // clear — we're executing now
    currentFilters.value = searchParams;
    
    const offset = reset ? 0 : pagination.value.offset + pagination.value.limit;
    
    const params = new URLSearchParams();
    if (searchParams.search) params.append('search', searchParams.search);
    if (searchParams.department) params.append('department', searchParams.department);
    if (searchParams.company) params.append('company', searchParams.company);
    if (searchParams.source) params.append('source', searchParams.source);
    if (searchParams.sort) params.append('sort', searchParams.sort);
    params.append('limit', '100');
    params.append('offset', offset.toString());

    const url = `${API_URL}/api/contacts?${params.toString()}`;
    const response = await authenticatedFetch(url);
    
    if (!response.ok) {
      throw new Error("Failed to fetch contacts");
    }
    
    const data = await response.json();
    
    if (reset) {
      contacts.value = data.contacts;
    } else {
      contacts.value = [...contacts.value, ...data.contacts];
    }
    
    pagination.value = data.pagination;
  } catch (error) {
    console.error("Error fetching contacts:", error);
  } finally {
    isLoading.value = false;
    
    // If another reset-fetch was queued while we were loading, execute it now
    if (pendingResetParams) {
      const queued = pendingResetParams;
      pendingResetParams = null;
      fetchContacts(queued, true);
    }
  }
}

/**
 * Load more contacts (for infinite scroll)
 */
async function loadMoreContacts() {
  if (!pagination.value.hasMore || isLoading.value) return;
  await fetchContacts(currentFilters.value, false);
}

/**
 * Fetch filter options (departments, companies) - optionally filtered by source
 */
async function fetchFilters(sourceId = null) {
  try {
    const params = new URLSearchParams();
    if (sourceId) params.append('source', sourceId);
    
    const url = `${API_URL}/api/contacts/filters${params.toString() ? '?' + params.toString() : ''}`;
    const response = await authenticatedFetch(url);
    
    if (!response.ok) {
      throw new Error("Failed to fetch filters");
    }
    
    filters.value = await response.json();
    return filters.value;
  } catch (error) {
    console.error("Error fetching filters:", error);
    return null;
  }
}

/**
 * Fetch initial presence data
 */
async function fetchPresence() {
  try {
    const response = await authenticatedFetch(`${API_URL}/api/presence`);
    
    if (!response.ok) {
      throw new Error("Failed to fetch presence");
    }
    
    presence.value = await response.json();
  } catch (error) {
    console.error("Error fetching presence:", error);
  }
}

/**
 * Fetch contact photo with caching
 * Uses localStorage to remember which users have no photo (survives refresh)
 * Uses in-memory cache for actual Blob URLs (cleared on refresh)
 * Silently returns null for users without photos (no console spam)
 */
async function fetchContactPhoto(email, entraId = null) {
  // Skip if no email or no entraId (non-Entra users don't have photos in Graph API)
  if (!email || !entraId) {
    return null;
  }

  // Skip service accounts (they never have photos)
  if (isServiceAccount(email)) {
    return null;
  }

  // Check persistent "no photo" cache first (avoids 404 requests after refresh)
  if (isInNoPhotoCache(email)) {
    return null;
  }

  // Check in-memory cache (Blob URLs from current session)
  if (photoCache.has(email)) {
    return photoCache.get(email);
  }

  try {
    const response = await authenticatedFetch(`${API_URL}/api/contacts/${encodeURIComponent(email)}/photo`);
    
    if (!response.ok) {
      // Add to persistent cache - don't request again until cache expires
      addToNoPhotoCache(email);
      photoCache.set(email, null);
      return null;
    }
    
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    photoCache.set(email, url);
    return url;
  } catch (error) {
    // Silently fail - add to "no photo" cache
    addToNoPhotoCache(email);
    photoCache.set(email, null);
    return null;
  }
}

/**
 * Check if email is likely a service account or resource without photo
 * These are never real users and will always return 404
 */
function isServiceAccount(email) {
  if (!email) return true;
  const lower = email.toLowerCase();
  
  // Explicit service account patterns
  const patterns = [
    'healthmailbox',
    'noreply',
    'service',
    'sync',
    'system',
    'mailbox',
    'admin@',
    '+adm@',         // Admin accounts like b.lindemeier+adm@
    'backoffice',
    'bewerbung',     // Application inbox
    'datenschutz',   // Privacy inbox
    'info@',
    'support@',
    'helpdesk',
    'dashboard@',
    'cert@',
    'consulting@',
    'celebration@',
    'beamer',        // Meeting room displays
    'besprechung',   // Meeting rooms
    'azurestack',    // Azure resources
    'autotask',      // Service integrations
    'checkmk',
    'cloudfest',
    'contabo',
    'bosch@',
    'dell@',
    'teams-',        // Teams service accounts
    'cloud@',
    'phonebook'
  ];
  
  return patterns.some(pattern => lower.includes(pattern));
}

/**
 * Clean up photo cache on page unload
 */
window.addEventListener('beforeunload', () => {
  photoCache.clear();
});

/**
 * Get presence for a contact by Entra ID
 */
function getPresence(entraId) {
  if (!entraId || !presence.value[entraId]) {
    return null;
  }
  return presence.value[entraId];
}

/**
 * Get presence CSS class (Tailwind classes)
 */
function getPresenceClass(entraId) {
  const p = getPresence(entraId);
  if (!p) return 'bg-slate-400';
  
  switch (p.availability) {
    case 'Available': return 'bg-emerald-500';
    case 'Busy': return 'bg-red-500';
    case 'DoNotDisturb': return 'bg-red-600';
    case 'Away': 
    case 'BeRightBack': return 'bg-amber-500';
    default: return 'bg-slate-400';
  }
}

/**
 * Get German presence label
 */
function getPresenceLabel(entraId) {
  const p = getPresence(entraId);
  if (!p) return 'Offline';
  
  const labels = {
    'Available': 'Verfügbar',
    'Busy': 'Beschäftigt',
    'DoNotDisturb': 'Nicht stören',
    'Away': 'Abwesend',
    'BeRightBack': 'Bin gleich zurück',
    'Offline': 'Offline',
    'OffWork': 'Nicht im Dienst'
  };
  
  // Check activity for more specific labels
  if (p.activity === 'InACall') return 'Im Anruf';
  if (p.activity === 'InAMeeting') return 'Im Meeting';
  if (p.activity === 'InAConferenceCall') return 'In Konferenz';
  if (p.activity === 'Presenting') return 'Präsentiert';
  if (p.activity === 'OutOfOffice') return 'Nicht im Büro';
  
  return labels[p.availability] || p.availability;
}

// ==================== CALL RECORDS FUNCTIONS ====================

/**
 * Fetch call history with filters
 * @param {Object} options - Filter options
 * @param {string} options.filter - 'all', 'missed', 'incoming', 'outgoing'
 * @param {number} options.days - Number of days to fetch (7, 30, etc.)
 * @param {string} options.queueEmail - Filter by specific Call Queue email
 * @param {boolean} options.personal - Filter only personal calls (no queue calls)
 * @param {boolean} reset - Whether to reset pagination
 */
async function fetchCallHistory(options = {}, reset = true) {
  if (callRecordsLoading.value) return;
  
  try {
    callRecordsLoading.value = true;
    currentCallFilter.value = options.filter || 'all';
    
    // Save options for loadMore
    if (reset) {
      currentCallOptions = { ...options };
    }
    
    const offset = reset ? 0 : callRecordsPagination.value.offset + callRecordsPagination.value.limit;
    
    const params = new URLSearchParams();
    if (options.filter && options.filter !== 'all') {
      params.append('filter', options.filter);
    }
    if (options.days) {
      params.append('days', options.days.toString());
    }
    // Queue filter: personal calls or specific queue
    if (options.personal) {
      params.append('personal', 'true');
    } else if (options.queueEmail) {
      params.append('queueEmail', options.queueEmail);
    }
    // Search filter
    if (options.search) {
      params.append('search', options.search);
    }
    // Note status filter
    if (options.noteStatus) {
      params.append('noteStatus', options.noteStatus);
    }
    // Agent filter
    if (options.answeredById) {
      params.append('answeredById', options.answeredById);
    }
    params.append('limit', '50');
    params.append('offset', offset.toString());
    
    const url = `${API_URL}/api/calls?${params.toString()}`;
    const response = await authenticatedFetch(url);
    
    if (!response.ok) {
      throw new Error('Failed to fetch call history');
    }
    
    const data = await response.json();
    
    if (reset) {
      callRecords.value = data.calls;
    } else {
      callRecords.value = [...callRecords.value, ...data.calls];
    }
    
    callRecordsPagination.value = data.pagination;
  } catch (error) {
    console.error('Error fetching call history:', error);
  } finally {
    callRecordsLoading.value = false;
  }
}

// Track current call filter options for loadMore
let currentCallOptions = { filter: 'all', days: 7 };

/**
 * Load more call records (for infinite scroll)
 */
async function loadMoreCallRecords() {
  if (!callRecordsPagination.value.hasMore || callRecordsLoading.value) return;
  await fetchCallHistory(currentCallOptions, false);
}

/**
 * Fetch user's Call Queue memberships
 */
async function fetchUserCallQueues() {
  if (userCallQueuesLoading.value) return;
  
  try {
    userCallQueuesLoading.value = true;
    const response = await authenticatedFetch(`${API_URL}/api/calls/queues`);
    
    if (!response.ok) {
      throw new Error('Failed to fetch call queues');
    }
    
    userCallQueues.value = await response.json();
  } catch (error) {
    console.error('Error fetching user call queues:', error);
    userCallQueues.value = [];
  } finally {
    userCallQueuesLoading.value = false;
  }
}

/**
 * Fetch missed calls count for badge
 */
async function fetchMissedCallsCount() {
  try {
    const response = await authenticatedFetch(`${API_URL}/api/calls/missed/count`);
    
    if (!response.ok) {
      throw new Error('Failed to fetch missed calls count');
    }
    
    const data = await response.json();
    missedCallsCount.value = data.count;
  } catch (error) {
    console.error('Error fetching missed calls count:', error);
  }
}

/**
 * Mark missed calls as seen (clear the badge)
 */
async function markMissedCallsSeen() {
  try {
    const response = await authenticatedFetch(`${API_URL}/api/calls/missed/seen`, {
      method: 'POST'
    });
    
    if (!response.ok) {
      throw new Error('Failed to mark calls as seen');
    }
    
    missedCallsCount.value = 0;
  } catch (error) {
    console.error('Error marking calls as seen:', error);
  }
}

/**
 * Format call duration as human-readable string
 * @param {number} seconds - Duration in seconds
 */
function formatCallDuration(seconds) {
  if (!seconds || seconds < 1) return 'Nicht angenommen';
  
  if (seconds < 60) {
    return `${seconds} Sek.`;
  }
  
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  
  if (minutes < 60) {
    return remainingSeconds > 0 ? `${minutes}:${remainingSeconds.toString().padStart(2, '0')} Min.` : `${minutes} Min.`;
  }
  
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return `${hours}:${remainingMinutes.toString().padStart(2, '0')} Std.`;
}

/**
 * Format call timestamp as date/time with relative time
 * @param {string} timestamp - ISO timestamp
 * @returns {object} Object with date, time, and relative properties
 */
function formatCallTime(timestamp) {
  if (!timestamp) return { display: '', relative: '', datetime: '' };
  
  const date = new Date(timestamp);
  const now = new Date();
  
  // Format date and time in Berlin timezone
  const timeStr = date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Berlin' });
  const dateStr = date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'Europe/Berlin' });
  
  // Calculate calendar day difference in Berlin timezone (not raw ms)
  // en-CA gives YYYY-MM-DD format which is reliably parseable
  const callDateBerlin = new Date(date.toLocaleDateString('en-CA', { timeZone: 'Europe/Berlin' }) + 'T00:00:00');
  const todayBerlin = new Date(now.toLocaleDateString('en-CA', { timeZone: 'Europe/Berlin' }) + 'T00:00:00');
  const diffDays = Math.round((todayBerlin - callDateBerlin) / (1000 * 60 * 60 * 24));
  
  // For intraday relative times, use actual elapsed time
  const diffMs = now - date;
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  
  // Calculate relative time based on calendar days
  let relative = '';
  if (diffDays === 0 && diffMinutes < 1) relative = 'Gerade eben';
  else if (diffDays === 0 && diffMinutes < 60) relative = `vor ${diffMinutes} Min.`;
  else if (diffDays === 0) relative = `vor ${diffHours} Std.`;
  else if (diffDays === 1) relative = 'Gestern';
  else if (diffDays < 7) relative = `vor ${diffDays} Tagen`;
  else relative = dateStr;
  
  return {
    display: relative,
    datetime: `${dateStr}, ${timeStr}`,
    time: timeStr,
    date: dateStr
  };
}

// ==================== OUTLOOK CONTACTS FUNCTIONS ====================

/**
 * Add a contact to the user's Outlook contacts
 * @param {Object} contact - The contact object to add
 */
async function addContactToOutlook(contact) {
  try {
    const response = await authenticatedFetch(`${API_URL}/api/contacts/add-to-outlook`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(contact)
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to add contact');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error adding contact to Outlook:', error);
    throw error;
  }
}

// Personal/My contacts state
const myContacts = ref([]);
const myContactsLoading = ref(false);
const myContactsLoaded = ref(false);

/**
 * Fetch user's personal Outlook contacts
 */
async function fetchMyContacts(search = '') {
  try {
    myContactsLoading.value = true;
    
    const params = new URLSearchParams();
    if (search) params.append('search', search);
    
    const url = `${API_URL}/api/contacts/my${params.toString() ? '?' + params.toString() : ''}`;
    const response = await authenticatedFetch(url);
    
    if (!response.ok) {
      throw new Error('Failed to fetch personal contacts');
    }
    
    const data = await response.json();
    myContacts.value = data.contacts;
    myContactsLoaded.value = true;
    
    return data.contacts;
  } catch (error) {
    console.error('Error fetching personal contacts:', error);
    myContacts.value = [];
    throw error;
  } finally {
    myContactsLoading.value = false;
  }
}

/**
 * Delete a contact from the user's Outlook contacts
 * @param {string} contactId - The Graph API contact ID to delete
 */
async function deleteContactFromOutlook(contactId) {
  try {
    const response = await authenticatedFetch(`${API_URL}/api/contacts/my/${contactId}`, {
      method: 'DELETE'
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to delete contact');
    }
    
    // Remove from local state
    myContacts.value = myContacts.value.filter(c => c.id !== contactId);
    
    return await response.json();
  } catch (error) {
    console.error('Error deleting contact from Outlook:', error);
    throw error;
  }
}

/**
 * Connect the WebSocket with the current auth token.
 * Call after authentication is complete (SSO or PKCE).
 */
function connectSocket() {
  const token = getAuthToken();
  if (!token) return;
  socket.auth = { token };
  if (!socket.connected) {
    socket.connect();
  }
}

/**
 * Reconnect socket with a fresh token (e.g. after token refresh)
 */
function reconnectSocket() {
  const token = getAuthToken();
  if (!token) return;
  socket.auth = { token };
  if (socket.connected) {
    socket.disconnect();
  }
  socket.connect();
}

// Legacy alias — components may still call this
function registerSocketUser() {
  connectSocket();
}

export {
  isConnected,
  contacts,
  presence,
  filters,
  pagination,
  isLoading,
  fetchContacts,
  loadMoreContacts,
  fetchFilters,
  fetchPresence,
  fetchContactPhoto,
  getPresence,
  getPresenceClass,
  getPresenceLabel,
  addContactToOutlook,
  deleteContactFromOutlook,
  myContacts,
  myContactsLoading,
  myContactsLoaded,
  fetchMyContacts,
  // Call Records
  callRecords,
  callRecordsLoading,
  callRecordsPagination,
  missedCallsCount,
  currentCallFilter,
  fetchCallHistory,
  loadMoreCallRecords,
  fetchMissedCallsCount,
  markMissedCallsSeen,
  formatCallDuration,
  formatCallTime,
  // Call Queue memberships
  userCallQueues,
  selectedCallQueue,
  userCallQueuesLoading,
  fetchUserCallQueues,
  // Assignment notifications
  callAssignments,
  callAssignmentCount,
  clearAssignmentCount,
  // Socket user registration (call after SSO to register for targeted notifications)
  registerSocketUser,
  connectSocket,
  reconnectSocket,
  // Socket instance (for direct event listeners in components)
  socket
};
